// src/services/recoveryEphemeralStore.js
// Secure ephemeral storage for recovery keys with AES-GCM encryption

import logger from '../utils/logger';

/**
 * Secure ephemeral storage for recovery keys
 * Uses in-memory storage with optional session persistence using AES-GCM
 */
class RecoveryEphemeralStore {
  constructor() {
    this.aesKey = null; // CryptoKey for AES-GCM
    this.memoryKey = null; // Uint8Array in memory
    this.sessionStorageKey = 'recovery/ek';
    this.initialized = false;
  }

  /**
   * Initialize the store with a new AES key
   */
  async init() {
    if (this.initialized) {
      logger.debug('RecoveryEphemeralStore already initialized');
      return;
    }

    // Check if crypto API is available
    const crypto = window.crypto || window.msCrypto;
    if (!crypto || !crypto.subtle) {
      const errorMsg = 'Web Crypto API is not available in this browser. Recovery key storage requires a secure browser environment.';
      logger.error(errorMsg);
      throw new Error(errorMsg);
    }

    try {
      // Generate AES-GCM key for session encryption
      this.aesKey = await crypto.subtle.generateKey(
        {
          name: 'AES-GCM',
          length: 256,
        },
        false, // Not extractable
        ['encrypt', 'decrypt'],
      );

      this.initialized = true;
      logger.debug('RecoveryEphemeralStore initialized');
    } catch (error) {
      logger.error('Failed to initialize RecoveryEphemeralStore:', error);
      throw new Error('Failed to initialize recovery key store');
    }
  }

  /**
   * Store recovery key in memory
   * @param {Uint8Array} keyBytes - The recovery key bytes
   */
  setKey(keyBytes) {
    if (!keyBytes || !(keyBytes instanceof Uint8Array)) {
      throw new Error('Invalid key format - must be Uint8Array');
    }

    // Zeroize previous key if exists
    if (this.memoryKey) {
      this.memoryKey.fill(0);
    }

    // Store new key (create copy to avoid external modifications)
    this.memoryKey = new Uint8Array(keyBytes);
    logger.debug('Recovery key stored in memory');
  }

  /**
   * Get recovery key from memory
   * @returns {Uint8Array|null} Copy of the key bytes or null
   */
  getKey() {
    if (!this.memoryKey) {
      // Try to load from session if available
      if (this.aesKey) {
        this.loadFromSession();
      }
    }

    if (!this.memoryKey) {
      return null;
    }

    // Return a copy to prevent external modifications
    return new Uint8Array(this.memoryKey);
  }

  /**
   * Persist encrypted key to session storage
   * @param {number} ttlMs - Time to live in milliseconds (max 5 minutes)
   */
  async persistToSession(ttlMs = 5 * 60 * 1000) {
    if (!this.initialized || !this.aesKey) {
      throw new Error('Store not initialized');
    }

    if (!this.memoryKey) {
      logger.warn('No key in memory to persist');
      return;
    }

    // Enforce maximum TTL of 5 minutes
    const maxTTL = 5 * 60 * 1000;
    const actualTTL = Math.min(ttlMs, maxTTL);

    try {
      // Generate random IV
      const crypto = window.crypto || window.msCrypto;
      const iv = crypto.getRandomValues(new Uint8Array(12));

      // Encrypt the key
      const encrypted = await crypto.subtle.encrypt(
        {
          name: 'AES-GCM',
          iv: iv,
        },
        this.aesKey,
        this.memoryKey,
      );

      // Store encrypted data with metadata
      const storageData = {
        iv: btoa(String.fromCharCode(...iv)),
        ct: btoa(String.fromCharCode(...new Uint8Array(encrypted))),
        ts: Date.now(),
        ttlMs: actualTTL,
      };

      sessionStorage.setItem(this.sessionStorageKey, JSON.stringify(storageData));
      logger.debug('Recovery key persisted to session storage with TTL:', actualTTL);

    } catch (error) {
      // Silently handle storage errors (quota, disabled, etc.)
      logger.debug('Failed to persist to session storage:', error.message);
    }
  }

  /**
   * Load and decrypt key from session storage
   * @returns {boolean} True if successfully loaded
   */
  loadFromSession() {
    if (!this.initialized || !this.aesKey) {
      return false;
    }

    try {
      const storedData = sessionStorage.getItem(this.sessionStorageKey);
      if (!storedData) {
        return false;
      }

      const data = JSON.parse(storedData);

      // Check TTL
      const elapsed = Date.now() - data.ts;
      if (elapsed > data.ttlMs) {
        logger.debug('Session storage entry expired');
        sessionStorage.removeItem(this.sessionStorageKey);
        return false;
      }

      // Decode base64
      const iv = Uint8Array.from(atob(data.iv), c => c.charCodeAt(0));
      const ciphertext = Uint8Array.from(atob(data.ct), c => c.charCodeAt(0));

      // Decrypt
      const crypto = window.crypto || window.msCrypto;
      crypto.subtle.decrypt(
        {
          name: 'AES-GCM',
          iv: iv,
        },
        this.aesKey,
        ciphertext,
      ).then(decrypted => {
        this.memoryKey = new Uint8Array(decrypted);
        logger.debug('Recovery key loaded from session storage');
      }).catch(error => {
        logger.error('Failed to decrypt session storage:', error);
        sessionStorage.removeItem(this.sessionStorageKey);
      });

      return true;

    } catch (error) {
      logger.debug('Failed to load from session storage:', error.message);
      // Remove corrupted entry
      try {
        sessionStorage.removeItem(this.sessionStorageKey);
      } catch {
        // Ignore
      }
      return false;
    }
  }

  /**
   * Clear all stored keys and reset state
   */
  clear() {
    // Zeroize memory key
    if (this.memoryKey) {
      this.memoryKey.fill(0);
      this.memoryKey = null;
    }

    // Clear session storage
    try {
      sessionStorage.removeItem(this.sessionStorageKey);
    } catch {
      // Ignore storage errors
    }

    // Clear AES key reference
    this.aesKey = null;
    this.initialized = false;

    logger.debug('RecoveryEphemeralStore cleared');
  }

  /**
   * Check if a key is currently stored
   * @returns {boolean}
   */
  hasKey() {
    return this.memoryKey !== null && this.memoryKey.length > 0;
  }

  /**
   * Get TTL info for session storage
   * @returns {Object|null} TTL info or null
   */
  getSessionTTL() {
    try {
      const storedData = sessionStorage.getItem(this.sessionStorageKey);
      if (!storedData) {
        return null;
      }

      const data = JSON.parse(storedData);
      const elapsed = Date.now() - data.ts;
      const remaining = Math.max(0, data.ttlMs - elapsed);

      return {
        ttlMs: data.ttlMs,
        remainingMs: remaining,
        expired: remaining === 0,
      };
    } catch {
      return null;
    }
  }
}

// Create singleton instance
const recoveryEphemeralStore = new RecoveryEphemeralStore();

// Clean up on window unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    recoveryEphemeralStore.clear();
  });
}

export default recoveryEphemeralStore;